import { fmtDeliverableType, type OutreachBrandState, type OutreachMoney, type OutreachRatePackage, type OutreachRatesRow } from '@discovr/types';
import { formatActivityTime } from './crmFormat';
import { fmtRate } from './QuoteBlock';

const UNKNOWN = '—';
export const UNASSIGNED_FORMAT = 'Unassigned';
export const QUOTE_AFTER_LOCK_LABEL = 'New quote after lock';

/** One line of a creator's rates: a format with its latest quote and lock, a package price, or a quote the parser could not place. */
export interface RateLine {
  key: string;
  format: string;
  quoted: string;
  locked: string;
}

const BARE_AMOUNT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Each amount in its own currency: rates are never converted, so a mixed row shows mixed currencies. An amount Go
 * could not name a currency for shows bare, since fmtRate would otherwise call it rupees.
 */
function moneyText(money: OutreachMoney | null): string {
  if (!money) return UNKNOWN;
  if (!money.currency) return BARE_AMOUNT.format(money.amount);
  return fmtRate(money.amount, money.currency);
}

/**
 * "2 × Non Collab Reel for": each format once with its count (an id repeats once per unit; the package quantity
 * multiplies it), the amount sitting in the next column. A count of 1 is left out.
 */
export function packageLabel(pkg: Pick<OutreachRatePackage, 'deliverableIds' | 'quantity'>): string {
  const counts = new Map<string, number>();
  for (const id of pkg.deliverableIds) counts.set(id, (counts.get(id) ?? 0) + pkg.quantity);
  const parts = [...counts].map(([id, count]) => (count > 1 ? `${count} × ${fmtDeliverableType(id)}` : fmtDeliverableType(id)));
  return `${parts.join(' + ')} for`;
}

export function rateLines(row: Pick<OutreachRatesRow, 'formats' | 'packages' | 'unassignedQuotes'>): RateLine[] {
  const formats = row.formats.map((format) => ({
    key: `format-${format.format}`,
    format: fmtDeliverableType(format.format),
    quoted: moneyText(format.quoted),
    locked: moneyText(format.locked),
  }));
  const packages = row.packages.map((pkg, index) => ({
    key: `package-${index}`,
    format: packageLabel(pkg),
    quoted: moneyText(pkg.price),
    locked: UNKNOWN,
  }));
  const unassigned = row.unassignedQuotes.map((quote, index) => ({
    key: `unassigned-${index}`,
    format: UNASSIGNED_FORMAT,
    quoted: moneyText(quote),
    locked: UNKNOWN,
  }));
  return [...formats, ...packages, ...unassigned];
}

/** "neha@getdiscovr.ai · Yesterday"; whichever half Go knows, or a dash. */
export function lockedByText(row: Pick<OutreachRatesRow, 'lockedBy' | 'lockedAt'>): string {
  const parts = [row.lockedBy, row.lockedAt ? formatActivityTime(row.lockedAt) : null].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : UNKNOWN;
}

/** "Revised 2×" once a rate was locked more than once; nothing for a first lock. */
export function revisedLabel(revisions: number): string | null {
  return revisions > 0 ? `Revised ${revisions}×` : null;
}

const BRAND_STATE_LABEL: Record<OutreachBrandState, string> = {
  none: UNKNOWN,
  pending: 'With brand',
  approved: 'Approved',
  counter_requested: 'Asked for less',
  rejected: 'Declined',
};

export function brandStateLabel(state: OutreachBrandState): string {
  return BRAND_STATE_LABEL[state];
}
